import { updateSession } from '@sentry/core';
import * as Sentry from '@sentry/react';
export var SentryTeam;
(function (SentryTeam) {
    SentryTeam["Unknown"] = "unknown";
    SentryTeam["Core"] = "core";
    SentryTeam["Routing"] = "routing-and-presence";
    SentryTeam["Insights"] = "insights";
    SentryTeam["Ecosystem"] = "ecosystem";
    SentryTeam["Partners"] = "partners";
    SentryTeam["Channels"] = "channels";
    SentryTeam["Messaging"] = "messaging";
    SentryTeam["Platform"] = "platform";
    SentryTeam["AI"] = "ai";
    SentryTeam["Conversations"] = "conversations";
    SentryTeam["Automation"] = "automation";
})(SentryTeam || (SentryTeam = {}));
const IGNORE_UNLESS_FATAL = [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
];
const BREADCRUMB_XHR_IGNORE = ['api2.amplitude.com'];
// Non-actionable errors that should never reach Sentry. Dropping these keeps the
// app-wide error-rate alert meaningful: a spike then corresponds to a real
// regression rather than transient network/extension noise.
const IGNORE_ERRORS = [
    ...IGNORE_UNLESS_FATAL,
    // Transient network failures (offline, cancelled requests, flaky connections).
    'Failed to fetch',
    'Load failed',
    'NetworkError when attempting to fetch resource.',
    'The network connection was lost.',
    'The request timed out.',
    // Code-splitting / stale-deploy chunk loading failures.
    'Loading chunk',
    'Loading CSS chunk',
    'ChunkLoadError',
    'Importing a module script failed.',
    'error loading dynamically imported module',
    // Browser extension / third-party script noise.
    'Non-Error promise rejection captured',
];
// Errors originating from these URLs are almost always browser extensions or
// injected third-party scripts we do not control, so they are not actionable.
const DENY_URLS = [
    /extensions\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    /^safari-web-extension:\/\//i,
];
// Pull the first uuid or numeric id out of a pathname so the event keeps the
// concrete entity (e.g. conversation id) that is otherwise stripped when the
// transaction is normalized for grouping.
const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
function extractEntityId(pathname) {
    const uuidMatch = pathname.match(UUID_PATTERN);
    if (uuidMatch) {
        return uuidMatch[0];
    }
    const digitsMatch = pathname.match(/\/(\d+)(?:\/|$)/);
    return digitsMatch?.[1];
}
export function sentryInit(environment, release) {
    Sentry.init({
        dsn: 'https://9a661fe0304d4712921ed43f509c3915@sentry.io/99229',
        environment,
        release,
        // This enables automatic instrumentation (highly recommended), but is not
        // necessary for purely manual usage
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.breadcrumbsIntegration({
                console: false,
            }),
        ],
        // To set a uniform sample rate. Disable to 0 by 6 Oct 2022 because of usage.
        tracesSampleRate: 0.0,
        // Drop non-actionable noise before it is sent so the app-wide error-rate
        // alert stays meaningful.
        ignoreErrors: IGNORE_ERRORS,
        denyUrls: DENY_URLS,
        beforeSend: (event) => {
            const { pathname } = location;
            // Keep the concrete entity id (conversation, etc.) and the raw route as
            // searchable tags so the Slack alert is actionable, while still grouping
            // issues by a normalized transaction.
            const entityId = extractEntityId(pathname);
            event.tags = {
                ...event.tags,
                route: pathname,
                ...(entityId ? { entity_id: entityId } : {}),
            };
            const values = event.exception?.values;
            event.transaction = pathname
                .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/<uuid>')
                .replace(/\/\d+/g, '/<digits>');
            if (values?.length) {
                const exception = values[0];
                const mechanism = exception.mechanism;
                if (event.level === 'fatal' && mechanism) {
                    // override sentry marking every event as handled
                    mechanism.handled = false;
                }
                if (event.level !== 'fatal' && exception.value) {
                    if (IGNORE_UNLESS_FATAL.includes(exception.value)) {
                        return null;
                    }
                    // Add the event value so we don't track it a second time.
                    IGNORE_UNLESS_FATAL.push(exception.value);
                }
            }
            return event;
        },
        beforeBreadcrumb: (breadcrumb) => {
            // nullify certain auto-breadcrumbs
            if (['xhr', 'fetch'].includes(breadcrumb.category || '') &&
                BREADCRUMB_XHR_IGNORE.some((url) => {
                    return breadcrumb.data?.url?.includes(url);
                })) {
                return null;
            }
            return breadcrumb;
        },
    });
}
export function sentrySetup(params) {
    Sentry.setUser({
        id: params.user.id,
    });
    Sentry.setContext('Organization', {
        connection_id: params.connectionId,
        org_avatar_url: params.organization.avatar_url,
        organization: params.organization.name,
        subdomain: params.organization.subdomain,
        org_id: params.organization.id,
    });
    // Promote the high-value org identifiers to searchable tags. Sentry alert
    // filters and Slack notifications operate on tags (not contexts), so this is
    // what lets us see and filter by the affected customer when the error-rate
    // alert fires.
    Sentry.setTag('subdomain', params.organization.subdomain);
    Sentry.setTag('org_id', String(params.organization.id));
    Sentry.setTag('connection_id', params.connectionId);
    Sentry.setTag('team', SentryTeam.Unknown);
}
export function sentrySetFatalScope(scope) {
    const session = scope.getSession();
    if (session) {
        updateSession(session, { status: 'crashed' });
        scope.setSession(session);
        scope.setLevel('fatal');
    }
}
export { Sentry };
//# sourceMappingURL=index.js.map